import { animated, useSpring } from 'react-spring'
import styled from 'styled-components'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { useToasts } from '@geist-ui/react'
import { useQuery, useSubscription } from '@apollo/client'
import { useAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { fadeAtom, ownsNFTAtom, videoElementAtom, videoSourceAtom } from './atoms'
import { rwBuySellDialogAtom, SECONDARY_ACTION } from '../../../atoms/SecondaryDialog'
import { Query_Root } from '../../../graphql/indexer-graphql-types'
import {
  BALANCE_SUBSCRIPTION,
  LISTINGS_QUERY,
  PRIMARY_SUBSCRIPTION,
  SECONDARY_USER_SUBSCRIPTION,
} from '../../../graphql/graphql-queries'
import { UnlockVideo } from '../../../data/UnlockVideo'
import { cancelFullscreen, checkIfFullscreen } from './utils'
import { VideoData } from '../../../interfaces'
import useAtomWithRef from '../../../utils/useAtomWithRef'
import { useConnectModal } from '../../../hooks/walletModal'
import { useWallet } from '@solana/wallet-adapter-react'
import { getTimeRemaining } from '../../../utils/time'
import useInterval from '../../../utils/useInterval'
import { buildUrl, CalendarEvent } from '../../../utils/calendar'
import isMobileCalendar from '../../../utils/isMobile'
import { Calendar } from '@geist-ui/react-icons'
import { Apple } from '@material-ui/icons'
import useAuth from '../../../hooks/auth'

import { usePrevious } from '../../../hooks/previous'
import { captureException } from '@sentry/nextjs'
import {
  ClickAddToCalendarInVideoPlayerEvent,
  ClickBuyInVideoPlayerEvent,
  ClickSellInVideoPlayerEvent,
} from '../../../events/revenue_events'
import log from '../../../events/log'
import { db } from '../../../data/FirebaseUtils'
import {
  checkoutAtom,
  crossmintBuyAtom,
  crossmintSecondaryAtom,
  PurchaseType,
  SecondaryAction,
} from '../../../atoms/Wallet'
import { fbEnrichUsersFromIds, fbGetUserMetadata } from '../../../data/FirebaseEnrich'
import { getShortenedAddress } from '../../../utils/address'

type TokengateButtonProps = {
  videoMetadata: VideoData
  scheduledTime: Date
}

/*======================== React component ===================================*/

const TokengateButton = ({ videoMetadata, scheduledTime }: TokengateButtonProps): JSX.Element => {
  const prevVideoAddress = usePrevious(videoMetadata.address)
  const [, setVideoSource] = useAtom(videoSourceAtom)
  const [ownsNFT, setOwnsNFT] = useAtom(ownsNFTAtom)
  const [videoElementRef] = useAtomWithRef(videoElementAtom)
  const [live, setIsLive] = useState(scheduledTime <= new Date())
  const [visible, setCalendarVisible] = useState(false)
  const wallet = useWallet()
  const [, crossmintBuy] = useAtom(crossmintBuyAtom)
  const [, crossmintSecondary] = useAtom(crossmintSecondaryAtom)
  const setVisible = useConnectModal()
  const { profile, provider, isAuthenticated } = useAuth()
  const prevIsAuthenticated = usePrevious(isAuthenticated)
  const [, setToast] = useToasts()
  const [buyHover, setBuyHover] = useState(false)
  const [hasAllowlist, setHasAllowlist] = useState(false)
  const [allowlistAmount, setAllowlistAmount] = useState(0)
  const [allowlistDiscount, setAllowlistDiscount] = useState<number | undefined>(undefined)
  const [, setOpen] = useAtom(rwBuySellDialogAtom)
  const [, checkout] = useAtom(checkoutAtom)
  const primaryData = useSubscription<Query_Root>(PRIMARY_SUBSCRIPTION, {
    variables: {
      mint: videoMetadata.address,
    },
  }).data
  const balanceData = useSubscription<Query_Root>(BALANCE_SUBSCRIPTION, {
    variables: {
      mint: videoMetadata.address,
      user: profile?.address ?? '',
    },
  }).data
  const listings = useQuery<Query_Root>(LISTINGS_QUERY, {
    variables: {
      mint: videoMetadata.address,
      offset: 0,
      limit: 1,
    },
  })
  const balance = balanceData?.mint[0]?.tokenAccountsAggregate.aggregate?.sum?.amount ?? 0
  const prevBalance = usePrevious(balance)

  const secondaryDataExclusingUser = useSubscription<Query_Root>(SECONDARY_USER_SUBSCRIPTION, {
    variables: {
      mint: videoMetadata.address,
      user: profile?.address ?? '',
    },
  }).data

  useInterval(() => {
    const time = getTimeRemaining(scheduledTime)
    if (time.days <= 0 && time.hours <= 0 && time.minutes <= 0 && time.seconds <= 0) {
      setIsLive(true)
    } else {
      setIsLive(false)
    }
  }, 1000)

  const checkAllowlist = async (user: string): Promise<void> => {
    const presaleDoc = await db.presales(videoMetadata.address).doc(user).get()
    setHasAllowlist(presaleDoc.exists)
    if (presaleDoc.exists) {
      const amount = presaleDoc.data()?.amount ?? 1
      const price = presaleDoc.data()?.price ?? undefined
      setAllowlistAmount(amount)
      setAllowlistDiscount(price)
    } else {
      setAllowlistAmount(0)
      setAllowlistDiscount(undefined)
    }
  }

  useEffect(() => {
    if (isAuthenticated && profile && videoMetadata.has_presale && !live) {
      checkAllowlist(profile.address)
    } else if (primaryData?.mint[0]?.editionMetadata?.primarySalePrice === 0) {
      setAllowlistAmount(5)
      setAllowlistDiscount(undefined)
    } else {
      setAllowlistAmount(0)
      setAllowlistDiscount(undefined)
    }
  }, [isAuthenticated, videoMetadata.address])

  const openCalendarLink = (event: CalendarEvent, type: string, id: string): void => {
    const url = buildUrl(event, type, id)
    if (!isMobileCalendar() && (url.startsWith('data') || url.startsWith('BEGIN'))) {
      const filename = 'download.ics'
      const blob = new Blob([url], { type: 'text/calendar;charset=utf-8' })

      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      window.open(url, '_blank')
    }
    setCalendarVisible(false)
  }

  const buyHoverState = useSpring({
    boxShadow: buyHover ? '0px 5px 25px rgba(255,255,255,0.2)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: buyHover ? `translate3d(0,-7%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const openCheckout = async (): Promise<void> => {
    if (!isAuthenticated || !wallet.publicKey) {
      setVisible(true)
      return
    }

    if (primaryData?.mint[0].supply === primaryData?.mint[0].editionMetadata?.maxSupply) {
      if (localStorage.getItem('walletName') === '"Crossmint"') {
        if (
          !secondaryDataExclusingUser?.mint[0].listingsWithTokenAggregate?.aggregate?.min?.price
        ) {
          crossmintSecondary({
            video: videoMetadata,
            type: SecondaryAction.OFFER,
          })
          return
        }
        const creator = await fbGetUserMetadata(videoMetadata.created_by)
        const { data } = await listings.refetch()
        const listingsResults = data.listingWithToken
        if (!listingsResults[0].seller) {
          return
        }
        crossmintBuy({
          video: videoMetadata,
          creatorName: creator?.display_name ?? getShortenedAddress(videoMetadata.created_by),
          purchaseType: PurchaseType.SECONDARY,
          buyer: wallet.publicKey?.toString(),
          seller: listingsResults[0].seller,
          price: (
            (secondaryDataExclusingUser?.mint[0].listingsWithTokenAggregate?.aggregate?.min
              ?.price ?? 0) / LAMPORTS_PER_SOL
          ).toFixed(2),
          priceLamports:
            secondaryDataExclusingUser?.mint[0].listingsWithTokenAggregate?.aggregate?.min?.price ??
            0,
          tokenSize: 1,
        })
        return
      }

      setOpen({ type: SECONDARY_ACTION.BUY, video: videoMetadata })
      return
    }

    const creator = await fbEnrichUsersFromIds([videoMetadata.created_by])
    checkout({
      data: {
        creator: creator.length > 0 ? creator[0] : undefined,
        video: videoMetadata,
        primaryInfo: primaryData
          ? {
              sold: primaryData?.mint[0]?.supply || 0,
              price: primaryData?.mint[0]?.editionMetadata?.primarySalePrice || 0,
              supply: primaryData?.mint[0]?.editionMetadata?.maxSupply || 0,
            }
          : undefined,
      },
      isLive: live,
      allowlistAmount,
      allowlistDiscount,
      balance,
    })
  }

  const sellEdition = async (): Promise<void> => {
    if (videoMetadata && wallet && wallet.connected && wallet.publicKey && provider) {
      try {
        if (localStorage.getItem('walletName') === '"Crossmint"') {
          crossmintSecondary({
            video: videoMetadata,
            type: SecondaryAction.LISTING,
          })
          return
        }
        setOpen({ type: SECONDARY_ACTION.SELL, video: videoMetadata! })
      } catch (err) {
        console.error(err)
        setToast({ text: `Oops, something went wrong. Please try again.`, type: 'error' })
        captureException(err)
      }
    } else {
      setVisible(true)
    }
  }

  const [, setFade] = useAtom(fadeAtom)

  const transitionVideoPlayer = useCallback(
    async (newSrc: string, address: string): Promise<void> => {
      setFade(true)
      const videoElement = videoElementRef.current!
      videoElement.pause()

      // The fade transition takes 200ms, so we need to wait for that.
      setTimeout(() => {
        const wasAtEnd = videoElement.duration == videoElement.currentTime
        const wasPaused = videoElement.paused
        const wasMuted = videoElement.muted

        const before = videoElement.currentTime

        setOwnsNFT(true)
        setVideoSource({
          src: newSrc,
          address: address,
        })
        videoElement.addEventListener(
          'loadeddata',
          () => {
            videoElementRef.current!.addEventListener(
              'timeupdate',
              () => {
                setFade(false)
              },
              { once: true }
            )
            videoElement.currentTime = before

            if (wasAtEnd || !wasPaused) {
              // Playing OR at end
              videoElement.play()
            } else {
              // Paused AND not at end
              videoElement.pause()
            }
            videoElement.muted = wasMuted
          },
          { once: true }
        )
      }, 200)
    },
    [setVideoSource]
  )

  const unlockVideo = useCallback(async (): Promise<void> => {
    try {
      const response = await UnlockVideo(videoMetadata.address)
      await transitionVideoPlayer(response.signed_url, videoMetadata.address)
    } catch (err) {
      console.error(err)
      console.log('LOCKING VIDEO')
      setOwnsNFT(false)
      setVideoSource(undefined)
      setFade(false)
      captureException(err)
    }
  }, [videoMetadata.address, transitionVideoPlayer, setOwnsNFT, setVideoSource, setFade])

  useEffect(() => {
    if (prevBalance === 0 && balance === 1) {
      // if you just bought the NFT, unlock it
      console.log('UNLOCKING/LOCKING — JUST BOUGHT')
      unlockVideo()
    } else if (prevBalance === 1 && balance === 0) {
      // if you just sold it, lock it
      console.log('UNLOCKING/LOCKING — JUST SOLD')
      unlockVideo()
    } else if (prevIsAuthenticated !== isAuthenticated) {
      // if you just connected/disconnected, unlock/lock the video
      console.log('UNLOCKING/LOCKING — AUTH CHANGED')
      unlockVideo()
    } else if (prevVideoAddress !== videoMetadata.address) {
      // if the video address has changed, unlock/lock the video
      console.log('UNLOCKING/LOCKING — NEW VIDEO')
      unlockVideo()
    }
  }, [
    balance,
    isAuthenticated,
    prevVideoAddress,
    prevBalance,
    prevIsAuthenticated,
    unlockVideo,
    videoMetadata.address,
  ])

  useEffect(() => {
    return () => {
      setVideoSource(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {live ? (
        <BuyButton
          onMouseEnter={() => setBuyHover(true)}
          onMouseLeave={() => setBuyHover(false)}
          style={buyHoverState}
          onClick={() => {
            if (checkIfFullscreen()) {
              cancelFullscreen()
            }

            //TODO: We don't distinguish between selling/listing and buying/offering.
            // This is due to the code structure we need to fix first.
            if (ownsNFT) {
              log(new ClickSellInVideoPlayerEvent())
              sellEdition()
            } else {
              log(new ClickBuyInVideoPlayerEvent())
              openCheckout()
            }
          }}
          secondary={false}
        >
          {ownsNFT
            ? secondaryDataExclusingUser?.mint[0].editionMetadata?.metadataObject
                ?.bidReceiptsAggregate?.aggregate?.max?.price
              ? `Sell Now`
              : `List For Sale`
            : primaryData?.mint[0].supply === primaryData?.mint[0].editionMetadata?.maxSupply
            ? secondaryDataExclusingUser?.mint[0].listingsWithTokenAggregate?.aggregate?.min
                ?.price ?? 0 > 0
              ? `Buy From Secondary`
              : 'Make Offer'
            : 'Buy Edition'}
        </BuyButton>
      ) : !videoMetadata.has_presale ||
        (videoMetadata.has_presale && isAuthenticated && !hasAllowlist) ? (
        <BuyButton
          onMouseEnter={() => setBuyHover(true)}
          onMouseLeave={() => setBuyHover(false)}
          style={buyHoverState}
          secondary={false}
          tabIndex={0}
          onFocus={() => setCalendarVisible(true)}
          onBlur={() => setCalendarVisible(false)}
          onClick={() => {
            log(new ClickAddToCalendarInVideoPlayerEvent())
          }}
        >
          {'Add To Calendar'}
          {visible && (
            <PopoverWrapper visible={visible}>
              <MenuItem
                onClick={() =>
                  openCalendarLink(
                    {
                      title: videoMetadata.title,
                      description: videoMetadata.description,
                      startTime: scheduledTime,
                    },
                    'apple',
                    videoMetadata.address
                  )
                }
              >
                <Apple htmlColor={'white'} />
                <MenuItemTitle color="white">Apple Calendar</MenuItemTitle>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  openCalendarLink(
                    {
                      title: videoMetadata.title,
                      description: videoMetadata.description,
                      startTime: scheduledTime,
                    },
                    'google',
                    videoMetadata.address
                  )
                }
              >
                <Calendar color={'white'} />
                <MenuItemTitle color="white">Google Calendar</MenuItemTitle>
              </MenuItem>
            </PopoverWrapper>
          )}
        </BuyButton>
      ) : (
        <BuyButton
          onMouseEnter={() => setBuyHover(true)}
          onMouseLeave={() => setBuyHover(false)}
          style={buyHoverState}
          onClick={() => openCheckout()}
          secondary={false}
        >
          {isAuthenticated && hasAllowlist
            ? `Claim ${allowlistAmount} Edition${allowlistAmount === 1 ? '' : 's'}`
            : 'Check Whitelist Status'}
        </BuyButton>
      )}
    </>
  )
}

export default TokengateButton

/*======================== Styled components =================================*/

const BuyButton = styled(animated.div)<{ secondary: boolean }>`
  background: white;
  width: 100%;
  border-radius: 15px;
  color: black;
  text-align: center;
  align-items: center;
  font-weight: 700;
  font-size: 15px;
  padding: 12px 0;
  cursor: pointer;
  pointer-events: all;
  min-width: 20px;

  @media (min-width: 43em) {
    font-size: 15px;
    padding: 18px 0;
    min-width: ${(props) => (props.secondary ? '' : '40%')};
  }

  @media (min-width: 62em) {
    font-size: 18px;
    min-width: ${(props) => (props.secondary ? '500px' : '')};
    width: 350px;
  }
`
const PopoverWrapper = styled.div<{ visible: boolean }>`
  position: absolute;
  z-index: 6;
  top: calc(100% + 5px);
  left: calc(50% - 100px - 11px);
  pointer-events: ${(props) => (props.visible ? 'all' : 'none')};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  min-width: 200px;
  background: black;
  border-radius: 5px;
  border-width: 1px;
  border-color: #333;
  border-style: solid;
`

const MenuItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: calc(100% - 20px);
  padding: 12px 20px;
  align-items: center;
  cursor: pointer;

  :hover {
    background: #232323;
  }
`

const MenuItemTitle = styled.div<{ color: string }>`
  font-weight: 400;
  color: ${(props) => props.color};
  font-size: 16px;
`
